import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DraggableProvided } from '@hello-pangea/dnd';
import { ApplicationState } from 'store';
import { AiManagementCard, AiManagementState } from 'store/modules/ai_management_store/types';
import { getColumns, removeCardLocal } from 'store/modules/ai_management_store/actions';
import { archiveCard, deleteCard } from 'services/actions/ai_management_cards';
import { formatDateTime } from 'services/ai_management_dates';
import AiManagementCardModal from 'components/organisms/ai_management_card_modal';
import AiManagementAvatar from 'components/atoms/ai_management_avatar';
import AiManagementUpdatesBell from 'components/atoms/ai_management_updates_bell';
import AiManagementCardMenu from 'components/molecules/ai_management_card_menu';
import AiManagementMembersModal from 'components/molecules/ai_management_members_modal';
import AiManagementArchiveBulkModal from 'components/molecules/ai_management_archive_bulk_modal';
import AiManagementConfirmModal from 'components/molecules/ai_management_confirm_modal';
import { Container } from './styles';

interface AiManagementKanbanCardProps {
  card: AiManagementCard;
  provided: DraggableProvided;
  isDragging: boolean;
}

const AiManagementKanbanCard: React.FC<AiManagementKanbanCardProps> = ({
  card,
  provided,
  isDragging,
}) => {
  const { bidding, members, counts } = card;
  const [modalOpen, setModalOpen] = useState(false);
  const [openAction, setOpenAction] = useState<'new_task' | 'new_update' | 'updates' | undefined>(
    undefined,
  );
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const dispatch = useDispatch();
  const filters = useSelector<ApplicationState, AiManagementState['filters']>(
    (state) => state.session.ai_management.filters,
  );

  const handleRemove = () => {
    setRemoving(true);
    deleteCard(card.id)
      .then((res) => {
        if (!res || res.status >= 400) return;
        setRemoveModalOpen(false);
        dispatch(removeCardLocal(card.id));
        dispatch(getColumns(filters));
      })
      .finally(() => setRemoving(false));
  };

  const openModal = (action?: 'new_task' | 'new_update' | 'updates') => {
    setOpenAction(action);
    setModalOpen(true);
  };

  const handleArchive = () => {
    setArchiving(true);
    archiveCard(card.id)
      .then((res) => {
        if (!res || res.status >= 400) return;
        setArchiveModalOpen(false);
        dispatch(removeCardLocal(card.id));
        dispatch(getColumns(filters));
      })
      .finally(() => setArchiving(false));
  };

  const dateRows = [
    { label: 'Prazo', iso: bidding.deadline_at },
    { label: 'Documento', iso: bidding.document_at },
    { label: 'Abertura', iso: bidding.opening_at },
  ].filter((row): row is { label: string; iso: string } => Boolean(row.iso));

  return (
    <>
      <Container
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className={`card mb-2 ${isDragging ? 'shadow' : ''}`}
        onClick={() => openModal()}
      >
        <div className="card-body p-2">
          <div className="d-flex align-items-start justify-content-between mb-2">
            <span className="badge badge-dark">{bidding.notice_number}</span>
            <AiManagementCardMenu
              ariaLabel={`Opções da licitação ${bidding.notice_number}`}
              items={[
                { label: 'Trabalhar na licitação', onClick: () => openModal() },
                { label: 'Alterar membros', onClick: () => setMembersModalOpen(true) },
                { label: 'Adicionar tarefa', onClick: () => openModal('new_task') },
                { label: 'Adicionar atualização', onClick: () => openModal('new_update') },
                {
                  label: 'Arquivar licitação',
                  onClick: () => setArchiveModalOpen(true),
                  dividerBefore: true,
                },
                {
                  label: 'Remover do Gestão IA',
                  onClick: () => setRemoveModalOpen(true),
                  danger: true,
                },
              ]}
            />
          </div>
          <div className="font-weight-bold text-dark">{bidding.public_body}</div>
          <div className="mb-1 card-location">
            {bidding.city} - {bidding.state}
          </div>
          <div className="text-muted mb-2 card-object">{bidding.object}</div>

          {dateRows.map((row) => (
            <div key={row.label}>
              <span className="font-weight-bold">{row.label}:</span> {formatDateTime(row.iso)}
            </div>
          ))}

          {(members.length > 0 || !!counts.updates) && (
            <div className="d-flex align-items-center mt-2">
              <AiManagementUpdatesBell
                count={counts.updates}
                onClick={() => openModal('updates')}
              />
              {members.length > 0 && (
                <div className="ml-auto d-flex align-items-center">
                  {members.map((member) => (
                    <AiManagementAvatar
                      key={member.id}
                      initials={member.initials}
                      memberId={member.id}
                      name={member.name}
                      size={32}
                      bordered
                      balloonPosition="up-right"
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Container>

      {modalOpen && (
        <AiManagementCardModal
          card={card}
          initialAction={openAction}
          onClose={() => setModalOpen(false)}
        />
      )}

      <AiManagementMembersModal
        card={card}
        show={membersModalOpen}
        onClose={() => setMembersModalOpen(false)}
      />

      <AiManagementArchiveBulkModal
        count={1}
        show={archiveModalOpen}
        saving={archiving}
        onClose={() => setArchiveModalOpen(false)}
        onConfirm={handleArchive}
      />

      <AiManagementConfirmModal
        show={removeModalOpen}
        title="Remover do Gestão IA"
        confirmLabel="Remover"
        savingLabel="Removendo..."
        variant="danger"
        saving={removing}
        onClose={() => setRemoveModalOpen(false)}
        onConfirm={handleRemove}
      >
        <p>
          Deseja mesmo remover a licitação {bidding.notice_number}? Ela irá sair do seu Gestão IA.
        </p>
      </AiManagementConfirmModal>
    </>
  );
};

export default AiManagementKanbanCard;
